import type { ThreadTask } from '@agentic-flow-studio/shared/components/clinic/ThreadTasksPanel';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clinicApi, retryUnlessRefused } from '@/utils/api';
import { trpc, trpcClient } from '@/utils/trpc';

/**
 * Labels/weights/category mappings, pre-resolved to the composite/flat keys
 * `clinic-logic.ts` reads directly — one call instead of separately fetching
 * the legacy REST metadata and the tag taxonomy and reconciling them here.
 * See `MailTagsService.getResolvedTriageConfig` for where the composite-key
 * (tag-projected) vs. flat-key (legacy, unreconnected) precedence is decided.
 */
export function useResolvedTriageConfig() {
  return trpc.mailTags.getResolvedTriageConfig.useQuery();
}

export function useClinicMailAccounts() {
  return useQuery({
    queryKey: ['clinic', 'mail-accounts'],
    queryFn: () => clinicApi.getMailAccounts(),
  });
}

export function useClinicThreads(accountEmails: string[]) {
  return useQuery({
    queryKey: ['clinic', 'threads', accountEmails],
    queryFn: () => clinicApi.getThreads(accountEmails),
  });
}

export function useClinicSummary(accountEmails: string[]) {
  return useQuery({
    queryKey: ['clinic', 'summary', accountEmails],
    queryFn: () => clinicApi.getSummary(accountEmails),
  });
}

export interface AnalyticsRange {
  from?: number;
  to?: number;
  accountEmails?: string[];
}

export function useAnalyticsOverview(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['clinic', 'analytics', 'overview', range],
    queryFn: () => clinicApi.getAnalyticsOverview(range),
  });
}

export function useAnalyticsByInbox(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['clinic', 'analytics', 'by-inbox', range],
    queryFn: () => clinicApi.getAnalyticsByInbox(range),
  });
}

export function useAnalyticsByUser(range: AnalyticsRange) {
  return useQuery({
    queryKey: ['clinic', 'analytics', 'by-user', range],
    queryFn: () => clinicApi.getAnalyticsByUser(range),
  });
}

export function useClinicCategories() {
  return useQuery({
    queryKey: ['clinic', 'categories'],
    queryFn: () => clinicApi.getCategories(),
  });
}

export function useClinicDomainWeights() {
  return useQuery({
    queryKey: ['clinic', 'domain-weights'],
    queryFn: () => clinicApi.getDomainWeights(),
  });
}

export function useCreateClinicTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      title,
      description,
      assignedTo,
      dueAt,
      priority,
      contactId,
    }: {
      conversationId: number;
      title?: string;
      description?: string;
      assignedTo?: number;
      dueAt?: string;
      priority?: number;
      contactId?: number;
    }) => {
      return trpcClient.tasks.create.mutate({
        label: title ?? '',
        content: description,
        assignedTo: assignedTo ?? null,
        conversationId,
        dueAt: dueAt ? Math.floor(new Date(dueAt).getTime() / 1000) : null,
        priority: priority ?? 0,
        contactId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useWorkspaceMembers() {
  return useQuery({
    queryKey: ['workspace', 'members'],
    queryFn: () => clinicApi.getWorkspaceMembers(),
  });
}

export function useArchiveClinicThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: number) => {
      return clinicApi.archiveConversation(conversationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
    },
  });
}

export function useResolveClinicThread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, resolved }: { conversationId: number; resolved: boolean }) => {
      return clinicApi.resolveConversation(conversationId, resolved);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic'] });
    },
  });
}

export function useClinicThread(conversationId: number | null) {
  return useQuery({
    queryKey: ['clinic', 'thread', conversationId],
    queryFn: () => clinicApi.getConversation(conversationId as number),
    enabled: conversationId !== null,
    // A 404 here is the answer -- the conversation is not one this person may open, or a
    // grant on it has lapsed. Retrying it turns an immediate, honest refusal into seven
    // seconds of spinner.
    retry: retryUnlessRefused,
  });
}

export function useClinicThreadMessages(conversationId: number | null) {
  return useQuery({
    queryKey: ['clinic', 'thread', conversationId, 'messages'],
    queryFn: () => clinicApi.getConversationMessages(conversationId as number),
    enabled: conversationId !== null,
    staleTime: 5 * 60_000,
    retry: retryUnlessRefused,
  });
}

export function useClinicThreadTasks(conversationId: number | null) {
  const membersQuery = useWorkspaceMembers();
  const tasksQuery = useQuery({
    queryKey: ['clinic', 'thread', conversationId, 'tasks'],
    queryFn: () =>
      trpcClient.tasks.getByConversation.query({ conversationId: conversationId as number }),
    enabled: conversationId !== null,
  });

  const data: ThreadTask[] | undefined = tasksQuery.data?.map((task): ThreadTask => {
    const member = membersQuery.data?.find((m) => m.id === task.assignedTo);
    return {
      id: task.id,
      label: task.label,
      content: task.content,
      status: task.status ?? 'todo',
      priority: task.priority ?? 0,
      dueAt: task.dueAt,
      assignedTo: task.assignedTo,
      assigneeName: member?.fullName ?? null,
      assigneeEmail: member?.email ?? null,
      createdAt: task.createdAt,
    };
  });

  return { ...tasksQuery, data };
}

export function useAutomationRules() {
  return useQuery({
    queryKey: ['clinic', 'rules'],
    queryFn: () => clinicApi.getRules(),
  });
}

export function useCreateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clinicApi.createRule,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'rules'] }),
  });
}

export function useUpdateRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...rule }: { id: number } & Parameters<typeof clinicApi.updateRule>[1]) =>
      clinicApi.updateRule(id, rule),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'rules'] }),
  });
}

export function useToggleRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isEnabled }: { id: number; isEnabled: boolean }) =>
      clinicApi.toggleRule(id, isEnabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'rules'] }),
  });
}

export function useDeleteRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clinicApi.deleteRule(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'rules'] }),
  });
}

export function useRuleExecutions(ruleId: number | null) {
  return useQuery({
    queryKey: ['clinic', 'rules', ruleId, 'executions'],
    queryFn: () => clinicApi.getRuleExecutions(ruleId as number),
    enabled: ruleId !== null,
  });
}

export function useCannedResponses() {
  return useQuery({
    queryKey: ['clinic', 'canned-responses'],
    queryFn: () => clinicApi.getCannedResponses(),
  });
}

export function useCreateCannedResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clinicApi.createCannedResponse,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'canned-responses'] }),
  });
}

export function useUpdateCannedResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: number } & Parameters<typeof clinicApi.updateCannedResponse>[1]) =>
      clinicApi.updateCannedResponse(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'canned-responses'] }),
  });
}

export function useDeleteCannedResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clinicApi.deleteCannedResponse(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clinic', 'canned-responses'] }),
  });
}
